import { LinkTarget } from '../../types/pdfjs-dist.type';
import { extract, parse, stringify } from 'query-string';
import { PRESENTATION_TYPE } from '../../constants/Constants';
import { RenderState } from '../../types/RenderState.type';
import { ViewerDeselectViewEvent, ViewerDownloadAssetEvent, ViewerEventType, ViewerPublicFocusChangeEvent, ViewerPublicRenderedPreviewFocusEvent, ViewerPublicRenderedViewFocusEvent, ViewerSelectViewEvent, ViewerThumbnailsOpenedChangedEvent } from '../../types/ViewerEvents.type';
import { AssetViewerViewMapper } from '../../helpers/AssetViewerViewMapper';
import { PdfViewer, PdfViewerEventType, PdfViewerSwitchLayoutEvent, PdfViewerThumbnailsOpenedChangeEvent, PdfViewerToolbarConfig, PdfViewerTranslationKey } from './../PdfViewer';
import { TranslationKeys } from '../../types/Translations.type';
export class PdfViewerPresentationRenderer {
    constructor(assetViewerView) {
        this.assetViewerView = assetViewerView;
        // Will not be destroyed !
        this.isActive = true;
        this.isLoading = false;
        this.pdfViewer = null;
        this.currentFocussedAssetViewerViewId = null;
        this.selectViewsEnabled = false;
        this.viewsToSelect = null;
        this.thumbnailsVisible = false;
        this.onSwitchLayout = (event) => {
            const { layout } = event.payload;
            this.switchedLayout = layout;
            if (this.pdfViewer) {
                this.pdfViewer.eventBus.dispatch(new PdfViewerSwitchLayoutEvent({ layout }));
            }
        };
        this.onDownloadAsset = (event) => {
            this.assetViewerView.eventBus.dispatch(new ViewerDownloadAssetEvent({ assetViewerItem: this.assetViewerView.assetViewerItem }));
        };
        this.resize = () => {
            if (this.pdfViewer) {
                this.pdfViewer.resize();
            }
        };
        this.onPdfViewerFocusChange = (event) => {
            this.assetViewerView.eventBus.dispatch(new ViewerPublicFocusChangeEvent({ assetViewerView: this.mapPdfViewerPageToPublicAssetViewerView(event.payload.pdfViewerPage) }));
        };
        this.onPdfViewerRenderedPreviewFocus = (event) => {
            this.assetViewerView.eventBus.dispatch(new ViewerPublicRenderedPreviewFocusEvent({
                assetViewerView: this.mapPdfViewerPageToPublicAssetViewerView(event.payload.pdfViewerPage)
            }));
        };
        this.onPdfViewerRenderedPageFocus = (event) => {
            this.assetViewerView.eventBus.dispatch(new ViewerPublicRenderedViewFocusEvent({
                assetViewerView: this.mapPdfViewerPageToPublicAssetViewerView(event.payload.pdfViewerPage)
            }));
        };
        this.onPdfViewerThumbnailsOpenedChange = (event) => {
            const { opened } = event.payload;
            this.assetViewerView.eventBus.dispatch(new ViewerThumbnailsOpenedChangedEvent({ opened }));
        };
        this.onSelectPage = (event) => {
            const assetViewerView = this.getAssetViewerViews().find(assetViewerView => assetViewerView.id === event.payload.pageId);
            if (assetViewerView) {
                this.assetViewerView.eventBus.dispatch(new ViewerSelectViewEvent({ assetViewerView }));
            }
        };
        this.onDeselectPage = (event) => {
            const assetViewerView = this.getAssetViewerViews().find(assetViewerView => assetViewerView.id === event.payload.pageId);
            if (assetViewerView) {
                this.assetViewerView.eventBus.dispatch(new ViewerDeselectViewEvent({ assetViewerView }));
            }
        };
        this.onViewerFocusChange = (event) => {
            this.currentFocussedAssetViewerViewId = event.payload.assetViewerView.id;
            if (!this.pdfViewerConfig) {
                return;
            }
            if (this.currentFocussedAssetViewerViewId === this.assetViewerView.id) {
                this.createPdfViewer();
            }
            else {
                this.destroyPdfViewer();
            }
        };
        this.onViewerViewRendered = (event) => {
            if (!this.pdfViewerConfig) {
                return;
            }
            if (this.currentFocussedAssetViewerViewId === this.assetViewerView.id) {
                this.createPdfViewer();
            }
        };
        this.onViewerThumbnailsOpenedChange = (event) => {
            const { opened } = event.payload;
            this.thumbnailsVisible = opened;
            if (this.pdfViewer) {
                this.pdfViewer.eventBus.dispatch(new PdfViewerThumbnailsOpenedChangeEvent({ opened }));
            }
        };
        this.onViewerThumbnailsOpenedChanged = (event) => {
            // We set this value because this can be called before the pdfviewer has been created.
            const { opened } = event.payload;
            this.thumbnailsVisible = opened;
        };
        this.createPdfViewer = () => {
            if (this.pdfViewer) {
                return;
            }
            if (!this.container || !this.container.parentElement) {
                this.createPdfViewerRequestAnimationFrameId = requestAnimationFrame(this.createPdfViewer);
                return;
            }
            this.container.classList.add('sp-pdf-viewer');
            // Ensure that we update the layout to persist it accross different pdfs
            this.pdfViewerConfig.layout = this.switchedLayout || this.pdfViewerConfig.layout;
            // Ensure that we enrich the selectedPageIds if viewsToSelect is defined
            if (this.viewsToSelect) {
                const selectedPageIds = [];
                this.viewsToSelect.forEach((viewToSelect) => {
                    if (this.pdfViewerConfig.id === viewToSelect.assetViewerItemId) {
                        selectedPageIds.push(`${viewToSelect.assetViewerItemId}-${viewToSelect.page}`);
                    }
                });
                this.pdfViewerConfig.selectedPageIds = selectedPageIds;
            }
            this.pdfViewer = new PdfViewer(this.pdfViewerConfig);
            this.pdfViewer.eventBus.on(PdfViewerEventType.FocusChange, this.onPdfViewerFocusChange);
            this.pdfViewer.eventBus.on(PdfViewerEventType.RenderedPreviewFocus, this.onPdfViewerRenderedPreviewFocus);
            this.pdfViewer.eventBus.on(PdfViewerEventType.RenderedPageFocus, this.onPdfViewerRenderedPageFocus);
            this.pdfViewer.eventBus.on(PdfViewerEventType.ThumbnailsOpenedChange, this.onPdfViewerThumbnailsOpenedChange);
            this.pdfViewer.eventBus.on(PdfViewerEventType.SelectPage, this.onSelectPage);
            this.pdfViewer.eventBus.on(PdfViewerEventType.DeselectPage, this.onDeselectPage);
            this.pdfViewer.eventBus.on(PdfViewerEventType.DownloadAsset, this.onDownloadAsset);
            this.pdfViewer.init(this.container, this.calculateInitialPageId(), this.thumbnailsVisible, this.selectViewsEnabled);
        };
        this.addEventListeners();
    }
    get url() {
        return '';
    }
    get dimensions() {
        return { width: 400, height: 400 };
    }
    get hasMetaData() {
        return !!this.pdfViewerConfig;
    }
    load() {
        if (!this.pdfViewerConfig) {
            /*
                If a suitable config cannot be determined an error is thrown
                and the AssetViewerView will fall back to an ErrorPresentationRenderer.
            */
            this.pdfViewerConfig = this.mapAssetViewerItemAndAssetViewerConfigToPdfViewerConfig(this.assetViewerView.assetViewerItem, this.assetViewerView.config);
        }
        return Promise.resolve();
    }
    render(container) {
        this.container = container;
        return Promise.resolve();
    }
    destroy() { }
    destroyFinal() {
        if (this.pdfViewer) {
            this.removeEventListeners();
            this.destroyPdfViewer();
        }
    }
    addEventListeners() {
        this.assetViewerView.eventBus.on(ViewerEventType.FocusChange, this.onViewerFocusChange);
        this.assetViewerView.eventBus.on(ViewerEventType.ViewRendered, this.onViewerViewRendered);
        this.assetViewerView.eventBus.on(ViewerEventType.ThumbnailsOpenedChange, this.onViewerThumbnailsOpenedChange);
        this.assetViewerView.eventBus.on(ViewerEventType.ThumbnailsOpenedChanged, this.onViewerThumbnailsOpenedChanged);
        this.assetViewerView.eventBus.on(ViewerEventType.SwitchLayout, this.onSwitchLayout);
    }
    removeEventListeners() {
        this.assetViewerView.eventBus.off(ViewerEventType.FocusChange, this.onViewerFocusChange);
        this.assetViewerView.eventBus.off(ViewerEventType.ViewRendered, this.onViewerViewRendered);
        this.assetViewerView.eventBus.off(ViewerEventType.ThumbnailsOpenedChange, this.onViewerThumbnailsOpenedChange);
        this.assetViewerView.eventBus.off(ViewerEventType.ThumbnailsOpenedChanged, this.onViewerThumbnailsOpenedChanged);
    }
    calculateInitialPageId() {
        let initialPageId = null;
        if (!this.initialPageNumber || this.initialPageNumber === 'FIRST') {
            initialPageId = Array.from(this.pdfViewerConfig.pages.values()).shift().id;
        }
        else if (this.initialPageNumber === 'LAST') {
            initialPageId = Array.from(this.pdfViewerConfig.pages.values()).pop().id;
        }
        else {
            this.pdfViewerConfig.pages.forEach(pageConfig => {
                if (pageConfig.pageNumber === this.initialPageNumber) {
                    initialPageId = pageConfig.id;
                }
            });
        }
        return initialPageId;
    }
    destroyPdfViewer() {
        cancelAnimationFrame(this.createPdfViewerRequestAnimationFrameId);
        if (!this.pdfViewer) {
            return;
        }
        this.pdfViewer.eventBus.off(PdfViewerEventType.FocusChange, this.onPdfViewerFocusChange);
        this.pdfViewer.eventBus.off(PdfViewerEventType.RenderedPreviewFocus, this.onPdfViewerRenderedPreviewFocus);
        this.pdfViewer.eventBus.off(PdfViewerEventType.RenderedPageFocus, this.onPdfViewerRenderedPageFocus);
        this.pdfViewer.eventBus.off(PdfViewerEventType.ThumbnailsOpenedChange, this.onPdfViewerThumbnailsOpenedChange);
        this.pdfViewer.eventBus.off(PdfViewerEventType.SelectPage, this.onSelectPage);
        this.pdfViewer.eventBus.off(PdfViewerEventType.DeselectPage, this.onDeselectPage);
        this.pdfViewer.eventBus.off(PdfViewerEventType.DownloadAsset, this.onDownloadAsset);
        // Ensure that the config is updated with any annotations that may have been added.
        this.pdfViewer.getPages().forEach((page) => {
            const pageConfig = this.pdfViewerConfig.pages.get(page.pageNumber);
            pageConfig.annotations = page.getAnnotations();
        });
        this.pdfViewer.destroy();
        this.pdfViewer = null;
    }
    mapAssetViewerItemAndAssetViewerConfigToPdfViewerConfig(assetViewerItem, config) {
        const { id, presentations } = assetViewerItem;
        if (!presentations) {
            throw 'PdfViewerPresentationRenderer :: AssetViewerItem does not have any presentations';
        }
        const originalPresentation = assetViewerItem.isPdf && presentations.find(presentation => presentation.type === PRESENTATION_TYPE.ORIGINAL);
        const processedPresentation = presentations.find(presentation => presentation.type === PRESENTATION_TYPE.PROCESSED);
        const previewPresentation = presentations.find(presentation => presentation.type === PRESENTATION_TYPE.PREVIEW_PAGE_PROGRESSIVE_LOCAL);
        const preferredPresentation = processedPresentation || originalPresentation;
        if (!preferredPresentation) {
            throw 'PdfViewerPresentationRenderer :: AssetViewerItem does not have a valid processed or original presentation';
        }
        if (!preferredPresentation.details) {
            throw 'PdfViewerPresentationRenderer :: AssetViewerItem presentation details are missing';
        }
        const url = preferredPresentation.urls ? preferredPresentation.urls[0] : undefined;
        if (!url) {
            throw 'PdfViewerPresentationRenderer :: AssetViewerItem does not have a processed or original presentation url';
        }
        const numPages = preferredPresentation.details.numpages;
        const fileSize = preferredPresentation.details.fileSize;
        let pageNumbers = [];
        if (assetViewerItem.pageRanges) {
            assetViewerItem.pageRanges.forEach((pageRange) => {
                for (let pageNumber = pageRange.start; pageNumber <= pageRange.end; pageNumber++) {
                    pageNumbers.push(pageNumber);
                }
            });
        }
        else if (assetViewerItem.page) {
            pageNumbers = [assetViewerItem.page];
        }
        else {
            if (!numPages) {
                throw 'PdfViewerPresentationRenderer :: AssetViewerItem presentation.details.numpages is missing';
            }
            for (let pageNumber = 1; pageNumber <= numPages; pageNumber++) {
                pageNumbers.push(pageNumber);
            }
        }
        let pages = new Map();
        if (!pageNumbers.length) {
            throw 'PdfViewerPresentationRenderer :: no pageNumbers could be calculated';
        }
        pageNumbers.forEach(pageNumber => {
            const pdfViewerConfigPage = {
                id: `${assetViewerItem.id}-${pageNumber}`,
                pageNumber,
                previewUrl: previewPresentation ? previewPresentation.urls[pageNumber] : null,
                dimensions: this.getDimensions(previewPresentation, pageNumber),
                annotations: (assetViewerItem.annotations && assetViewerItem.annotations[pageNumber]) || null,
                embeddedData: (assetViewerItem.embeddedData && assetViewerItem.embeddedData.find(embeddedData => embeddedData.page === pageNumber)) ||
                    null,
                canCreateAnnotations: config.CAN_CREATE_ANNOTATIONS,
                debug: config.DEBUG
            };
            pages.set(pageNumber, pdfViewerConfigPage);
        });
        const translations = {
            [PdfViewerTranslationKey.Close]: config.TRANSLATIONS[TranslationKeys.Close],
            [PdfViewerTranslationKey.Download]: config.TRANSLATIONS[TranslationKeys.Download],
            [PdfViewerTranslationKey.RenderError]: config.TRANSLATIONS[TranslationKeys.RenderError],
            [PdfViewerTranslationKey.RenderErrorWhenOffline]: config.TRANSLATIONS[TranslationKeys.RenderErrorWhenOffline],
            [PdfViewerTranslationKey.ToggleThumbnails]: config.TRANSLATIONS[TranslationKeys.ToggleThumbnails],
            [PdfViewerTranslationKey.SwitchToSinglePageView]: config.TRANSLATIONS[TranslationKeys.SwitchToSinglePageView],
            [PdfViewerTranslationKey.SwitchToContinuousScrollView]: config.TRANSLATIONS[TranslationKeys.SwitchToContinuousScrollView],
            [PdfViewerTranslationKey.ZoomIn]: config.TRANSLATIONS[TranslationKeys.ZoomIn],
            [PdfViewerTranslationKey.ZoomOut]: config.TRANSLATIONS[TranslationKeys.ZoomOut],
            [PdfViewerTranslationKey.SearchComponentNoResults]: config.TRANSLATIONS[TranslationKeys.SearchComponentNoResults],
            [PdfViewerTranslationKey.SearchComponentSearchDocument]: config.TRANSLATIONS[TranslationKeys.SearchComponentSearchDocument],
            [PdfViewerTranslationKey.SearchComponentIsUnavailable]: config.TRANSLATIONS[TranslationKeys.SearchComponentIsUnavailable]
        };
        let searchConfig;
        if (config.pdfViewerSearchConfig) {
            searchConfig = Object.assign(Object.assign({}, config.pdfViewerSearchConfig), { id: assetViewerItem.assetFileId });
        }
        let pendoConfig;
        if (config.pdfViewerPendoConfig) {
            pendoConfig = Object.assign(Object.assign({}, config.pdfViewerPendoConfig), { defaultTrackEventMetadata: Object.assign(Object.assign({}, config.pdfViewerPendoConfig.defaultTrackEventMetadata), { assetFileId: assetViewerItem.assetFileId }) });
        }
        const pdfViewerConfig = {
            id,
            url,
            name: assetViewerItem.displayName,
            fileSize,
            defaultRangeChunkSize: config.PDFJS_RANGE_CHUNK_SIZE,
            minRangeChunkSize: config.PDFJS_MIN_RANGE_CHUNK_SIZE,
            maxRangeChunkSize: config.PDFJS_MAX_RANGE_CHUNK_SIZE,
            externalLinkTarget: LinkTarget.BLANK,
            translations,
            tintColor: config.TINT_COLOR,
            thumbnailContainer: config.thumbnailContainer,
            pages,
            getPdfJs: () => config.getPdfJs(),
            getPdfJsWorker: () => config.getPdfJsWorker(),
            refreshToken: () => config.refreshToken(),
            pageRanges: assetViewerItem.pageRanges,
            lockedPages: (assetViewerItem.nodeItem && assetViewerItem.nodeItem.asset && assetViewerItem.nodeItem.asset.lockedPages) || null,
            canZoom: config.canZoom,
            maxZoomScale: config.MAX_ZOOM_SCALE,
            minZoomScale: config.MIN_ZOOM_SCALE,
            disableDownload: config.IS_WINDOWS_DESKTOP_APP,
            debug: config.DEBUG,
            downloadUrl: this.createDownloadUrl(assetViewerItem.downloadUrl, assetViewerItem.displayName),
            layout: config.PDF_VIEWER_LAYOUT,
            searchConfig,
            toolbarConfig: this.getPdfViewerToolbarConfig(config),
            pendoConfig
        };
        return pdfViewerConfig;
    }
    getPdfViewerToolbarConfig(config) {
        let toolbarConfig = new PdfViewerToolbarConfig();
        if (config.PDF_VIEWER_TOOLBAR_UNAVAILABLE_ACTIONS) {
            toolbarConfig.actions = toolbarConfig.actions.filter(action => !config.PDF_VIEWER_TOOLBAR_UNAVAILABLE_ACTIONS.includes(action));
            if (!toolbarConfig.actions.length) {
                toolbarConfig = undefined;
            }
        }
        return toolbarConfig;
    }
    createDownloadUrl(downloadUrl, filename) {
        if (!downloadUrl) {
            return;
        }
        const urlParams = Object.assign(Object.assign({}, parse(extract(downloadUrl))), { filename });
        return `${downloadUrl.split('?')[0]}?${stringify(urlParams)}`;
    }
    getDimensions(presentation, page) {
        if (!presentation) {
            return null;
        }
        if (presentation.details &&
            presentation.details.dimensions &&
            presentation.details.dimensions.exceptions &&
            presentation.details.dimensions.exceptions[page]) {
            return {
                width: presentation.details.dimensions.exceptions[page].width,
                height: presentation.details.dimensions.exceptions[page].height
            };
        }
        if (presentation.details &&
            presentation.details.dimensions &&
            presentation.details.dimensions.width &&
            presentation.details.dimensions.height) {
            return {
                width: presentation.details.dimensions.width,
                height: presentation.details.dimensions.height
            };
        }
        return null;
    }
    mapPdfViewerPageToPublicAssetViewerView(pdfViewerPage) {
        return {
            id: pdfViewerPage.id,
            page: pdfViewerPage.pageNumber,
            previewRenderState: pdfViewerPage.previewRenderState,
            renderState: pdfViewerPage.renderState,
            getAnnotationHelper: pdfViewerPage.getAnnotationHelper,
            getAnnotations: pdfViewerPage.getAnnotations,
            didAnnotationsChange: pdfViewerPage.didAnnotationsChange,
            enableAnnotations: pdfViewerPage.enableAnnotations,
            disableAnnotations: pdfViewerPage.disableAnnotations,
            assetViewerItem: this.assetViewerView.assetViewerItem // Add Trojaans paard
        };
    }
    mapPdfViewerPageConfigToPublicAssetViewerView(pageConfig) {
        return {
            id: pageConfig.id,
            page: pageConfig.pageNumber,
            previewRenderState: RenderState.None,
            renderState: RenderState.None,
            getAnnotationHelper: () => { },
            getAnnotations: () => { },
            didAnnotationsChange: () => { },
            enableAnnotations: () => { },
            disableAnnotations: () => { },
            assetViewerItem: this.assetViewerView.assetViewerItem
        };
    }
    getAssetViewerViews() {
        if (this.pdfViewer) {
            return this.pdfViewer.getPages().map(pdfViewerPage => this.mapPdfViewerPageToPublicAssetViewerView(pdfViewerPage));
        }
        if (this.pdfViewerConfig) {
            return Array.from(this.pdfViewerConfig.pages.values()).map((pdfViewerPageConfig) => this.mapPdfViewerPageConfigToPublicAssetViewerView(pdfViewerPageConfig));
        }
        return [AssetViewerViewMapper.mapToPublicAssetViewerView(this.assetViewerView)];
    }
    enableViewSelection() {
        // We set this value because this can be called before the pdfviewer has been created.
        this.selectViewsEnabled = true;
        if (this.pdfViewer) {
            this.pdfViewer.enablePageSelection();
        }
    }
    disableViewSelection() {
        this.selectViewsEnabled = false;
        if (this.pdfViewer) {
            this.pdfViewer.disablePageSelection();
        }
    }
    selectAllViews() {
        if (this.pdfViewer && this.pdfViewer.thumbnailViewer) {
            this.pdfViewer.thumbnailViewer.selectAllPages();
        }
    }
    deselectAllViews() {
        if (this.pdfViewer && this.pdfViewer.thumbnailViewer) {
            this.pdfViewer.thumbnailViewer.deselectAllPages();
        }
    }
    setViewsToSelect(viewsToSelect) {
        this.viewsToSelect = viewsToSelect;
    }
}
//# sourceMappingURL=PdfViewerPresentationRenderer.js.map